import React, { useState, useEffect } from "react";
import { Popover, Drawer, Select } from "antd";
import { CalendarOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import useIsMobile from "./useIsMobile";
import {
  CALENDAR_HIJRI,
  CALENDAR_GREGORIAN,
  HIJRI_MONTHS_AR,
  HIJRI_MONTHS_EN,
  GREGORIAN_MONTHS_AR,
  GREGORIAN_MONTHS_EN,
  WEEKDAYS_AR,
  WEEKDAYS_EN,
  addDays,
  formatDualDate,
  hijriMonthInfo,
  hijriParts,
  isArabicLang,
  isHijriSupported,
  parseIso,
  resolvePrimaryCalendar,
  todayIso,
  toIso,
} from "../../utils/eventDate";

// Date picker for event dates. Value in/out is a plain Gregorian "YYYY-MM-DD" string, so it drops
// into Form.Item unchanged. The popup is a calendar with a Hijri / Gregorian switch; whichever
// calendar is chosen leads in the field and the other is shown under it, always aligned because
// the Hijri date is derived from the stored Gregorian one (Umm al-Qura). Picking a day applies
// immediately - no confirm button. Popover on desktop, bottom sheet on phones.
//
// Optional props (wired for the event's own dates, left off elsewhere):
//   calendar / onCalendarChange   - the event's leading calendar ("hijri" | "gregorian")
//   adjustment / onAdjustmentChange / showAdjustment - -1/0/+1 correction of the shown Hijri date

const toDigits = (n, arabicIndic) => (arabicIndic ? String(n).replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]) : String(n));

const daysInGregorianMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const DateField = ({
  value,
  onChange,
  calendar,
  onCalendarChange,
  adjustment = 0,
  onAdjustmentChange,
  showAdjustment = false,
  placeholder,
  primaryColor = "#4f46e5",
  minDate, // optional "YYYY-MM-DD": earlier days are shown but can't be picked
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isArabic = isArabicLang(lang);
  const isMobile = useIsMobile();
  const hijriAvailable = isHijriSupported();
  const primaryCalendar = hijriAvailable ? resolvePrimaryCalendar(calendar, lang) : CALENDAR_GREGORIAN;

  const [open, setOpen] = useState(false);
  const [viewCalendar, setViewCalendar] = useState(primaryCalendar);
  const [view, setView] = useState({ year: 2026, month: 1 });

  const selectedDate = parseIso(value);
  const text = formatDualDate(value, lang, calendar, adjustment);

  // Where a given day sits in a calendar's year/month terms.
  const viewOf = (utcDate, cal) => {
    if (cal === CALENDAR_HIJRI) {
      const p = hijriParts(utcDate, adjustment);
      if (p) return { year: p.year, month: p.month };
    }
    return { year: utcDate.getUTCFullYear(), month: utcDate.getUTCMonth() + 1 };
  };

  // Each time the popup opens, start on the selected date (or today) in the leading calendar.
  useEffect(() => {
    if (!open) return;
    const min = minDate ? parseIso(minDate) : null;
    const anchor = selectedDate || (min && toIso(min) > todayIso() ? min : parseIso(todayIso()));
    setViewCalendar(primaryCalendar);
    setView(viewOf(anchor, primaryCalendar));
  }, [open]);

  const months = viewCalendar === CALENDAR_HIJRI
    ? (isArabic ? HIJRI_MONTHS_AR : HIJRI_MONTHS_EN)
    : (isArabic ? GREGORIAN_MONTHS_AR : GREGORIAN_MONTHS_EN);

  // Grid for the month being viewed: where the 1st falls, how long it is, and each day's Gregorian date.
  const monthInfo = (() => {
    if (viewCalendar === CALENDAR_HIJRI) return hijriMonthInfo(view.year, view.month, adjustment);
    return {
      first: new Date(Date.UTC(view.year, view.month - 1, 1)),
      length: daysInGregorianMonth(view.year, view.month),
    };
  })();

  const todayYear = viewOf(parseIso(todayIso()), viewCalendar).year;
  const yearOptions = (() => {
    const years = new Set([view.year]);
    for (let y = todayYear - 2; y <= todayYear + 10; y += 1) years.add(y);
    return [...years].sort((a, b) => a - b).map((y) => ({
      value: y,
      label: toDigits(y, isArabic && viewCalendar === CALENDAR_HIJRI),
    }));
  })();

  const step = (delta) => {
    setView((v) => {
      const index = v.year * 12 + (v.month - 1) + delta;
      return { year: Math.floor(index / 12), month: (index % 12) + 1 };
    });
  };

  const switchCalendar = (next) => {
    if (next === viewCalendar) return;
    // Keep the same moment on screen: re-express the first of the current view in the other calendar.
    const anchor = selectedDate || monthInfo?.first || parseIso(todayIso());
    setViewCalendar(next);
    setView(viewOf(anchor, next));
    onCalendarChange?.(next); // the chosen calendar becomes the event's leading one
  };

  const isBeforeMin = (utcDate) => !!minDate && toIso(utcDate) < minDate;

  const pick = (utcDate) => {
    if (isBeforeMin(utcDate)) return;
    onChange?.(toIso(utcDate));
    setOpen(false);
  };

  const weekdays = isArabic ? WEEKDAYS_AR : WEEKDAYS_EN;
  const todayDate = parseIso(todayIso());
  const PrevIcon = isArabic ? RightOutlined : LeftOutlined;
  const NextIcon = isArabic ? LeftOutlined : RightOutlined;
  const useArabicIndic = isArabic && viewCalendar === CALENDAR_HIJRI;

  const navButton = {
    width: 32, height: 32, border: "none", background: "transparent", borderRadius: 8,
    cursor: "pointer", color: "#555",
  };

  const content = (
    <div dir={isArabic ? "rtl" : "ltr"} style={{ width: isMobile ? "100%" : 300 }}>
      {hijriAvailable && (
        <div style={{ display: "flex", background: "#eef0f6", borderRadius: 10, padding: 3, marginBottom: 12 }}>
          {[CALENDAR_HIJRI, CALENDAR_GREGORIAN].map((cal) => (
            <button
              key={cal}
              type="button"
              onClick={() => switchCalendar(cal)}
              style={{
                flex: 1, height: 34, border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600,
                background: viewCalendar === cal ? primaryColor : "transparent",
                color: viewCalendar === cal ? "#fff" : "#555",
              }}
            >
              {t(cal === CALENDAR_HIJRI ? "datePicker.hijri" : "datePicker.gregorian")}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 8 }}>
        <button type="button" style={navButton} onClick={() => step(-1)} aria-label={t("datePicker.previousMonth")}>
          <PrevIcon />
        </button>
        <div style={{ display: "flex", gap: 6, flex: 1, justifyContent: "center" }}>
          <Select
            value={view.month}
            onChange={(m) => setView((v) => ({ ...v, month: m }))}
            options={months.map((name, i) => ({ value: i + 1, label: name }))}
            style={{ minWidth: 118 }}
            popupMatchSelectWidth={false}
          />
          <Select
            value={view.year}
            onChange={(y) => setView((v) => ({ ...v, year: y }))}
            options={yearOptions}
            style={{ minWidth: 86 }}
            popupMatchSelectWidth={false}
          />
        </div>
        <button type="button" style={navButton} onClick={() => step(1)} aria-label={t("datePicker.nextMonth")}>
          <NextIcon />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center", fontSize: 12, marginBottom: 4 }}>
        {weekdays.map((w, i) => (
          // Friday and Saturday are the Saudi weekend.
          <div key={w} style={{ padding: "4px 0", color: i === 5 || i === 6 ? "#d6457a" : "#8c8c99", fontWeight: 600 }}>{w}</div>
        ))}
      </div>

      {monthInfo ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {Array.from({ length: monthInfo.first.getUTCDay() }).map((_, i) => <div key={`blank-${i}`} />)}
          {Array.from({ length: monthInfo.length }).map((_, i) => {
            const date = addDays(monthInfo.first, i);
            const isSelected = !!selectedDate && toIso(date) === toIso(selectedDate);
            const isToday = toIso(date) === toIso(todayDate);
            const disabled = isBeforeMin(date);
            return (
              <button
                key={i}
                type="button"
                disabled={disabled}
                onClick={() => pick(date)}
                style={{
                  height: 38, border: isToday && !isSelected ? `1px solid ${primaryColor}` : "1px solid transparent",
                  borderRadius: 10, cursor: disabled ? "not-allowed" : "pointer", fontSize: 14,
                  background: isSelected ? primaryColor : "transparent",
                  color: isSelected ? "#fff" : disabled ? "#d0d0d6" : "rgba(0,0,0,0.88)",
                  fontWeight: isSelected ? 700 : 400,
                }}
              >
                {toDigits(i + 1, useArabicIndic)}
              </button>
            );
          })}
        </div>
      ) : (
        <div style={{ padding: 16, textAlign: "center", color: "#8c8c99" }}>{t("datePicker.unavailable")}</div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          type="button"
          disabled={isBeforeMin(todayDate)}
          onClick={() => pick(todayDate)}
          style={{ flex: 1, height: 38, borderRadius: 10, border: "1px solid #d9d9d9", background: "#fff", cursor: isBeforeMin(todayDate) ? "not-allowed" : "pointer", opacity: isBeforeMin(todayDate) ? 0.5 : 1, fontWeight: 600 }}
        >
          {t("datePicker.today")}
        </button>
        <button
          type="button"
          onClick={() => { onChange?.(undefined); setOpen(false); }}
          style={{ flex: 1, height: 38, borderRadius: 10, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer", fontWeight: 600 }}
        >
          {t("datePicker.clear")}
        </button>
      </div>
    </div>
  );

  const trigger = (
    <div className="time-input-wrap">
      <div
        role="button"
        tabIndex={0}
        className="native-date-input time-input-display"
        style={{ cursor: "pointer" }}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={isMobile ? () => setOpen((o) => !o) : undefined}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        {...rest}
      >
        <span style={{ color: text.primary ? undefined : "#bfbfbf" }}>
          {text.primary || placeholder || t("datePicker.placeholder")}
        </span>
        <CalendarOutlined style={{ color: "#8c8c8c" }} />
      </div>
    </div>
  );

  // Under the field: the other calendar's date, and (event dates only) the +/-1 Hijri correction.
  const showAdjust = showAdjustment && hijriAvailable && !!selectedDate;
  const stepButton = (disabled) => ({
    width: 28, height: 28, borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff",
    cursor: disabled ? "not-allowed" : "pointer", fontSize: 16, lineHeight: 1,
  });
  const caption = (text.secondary || showAdjust) && (
    <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
      {text.secondary && <div>{text.secondary}</div>}
      {showAdjust && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <span>{t("datePicker.adjustHijri")}</span>
          <button
            type="button"
            disabled={adjustment <= -1}
            onClick={() => onAdjustmentChange?.(adjustment - 1)}
            aria-label={t("datePicker.hijriMinusOne")}
            style={stepButton(adjustment <= -1)}
          >
            −
          </button>
          <span style={{ minWidth: 22, textAlign: "center", fontWeight: 600, color: adjustment ? primaryColor : undefined }} dir="ltr">
            {adjustment > 0 ? "+" + adjustment : adjustment}
          </span>
          <button
            type="button"
            disabled={adjustment >= 1}
            onClick={() => onAdjustmentChange?.(adjustment + 1)}
            aria-label={t("datePicker.hijriPlusOne")}
            style={stepButton(adjustment >= 1)}
          >
            +
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {isMobile ? (
        <>
          {trigger}
          <Drawer
            placement="bottom"
            height="auto"
            open={open}
            onClose={() => setOpen(false)}
            destroyOnClose
            title={t("datePicker.placeholder")}
            styles={{ body: { paddingBottom: 24 } }}
          >
            {content}
          </Drawer>
        </>
      ) : (
        <Popover
          open={open}
          onOpenChange={setOpen}
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          destroyTooltipOnHide
          content={content}
        >
          {trigger}
        </Popover>
      )}
      {caption}
    </div>
  );
};

// Plain one-line text for places that only need the date (e.g. the review step).
export const formatDate = (dateStr, lang, calendar, adjustment = 0) => {
  const { primary, secondary } = formatDualDate(dateStr, lang, calendar, adjustment);
  if (!primary) return "";
  return secondary ? `${primary} · ${secondary}` : primary;
};

export default DateField;
